import React, { createContext, useContext, useState, useCallback } from "react";
import Snackbar from "components/common/Snackbar";

const SnackbarContext = createContext();

export const useSnackbar = () => {
    const context = useContext(SnackbarContext);
    if (!context) {
        throw new Error("useSnackbar must be used within a SnackbarProvider");
    }
    return context;
};

export const SnackbarProvider = ({ children }) => {
    const [snackbar, setSnackbar] = useState({
        isVisible: false,
        title: "",
        buttonText: "",
        type: "success",
        duration: 3000,
        bottomPosition: 90, // 기본값 추가
    });

    const showSnackbar = useCallback((title, options = {}) => {
        const {
            buttonText = "",
            type = "success",
            duration = 3000,
            onButtonClick = null,
            bottomPosition = 90, // 기본값 90px
        } = options;

        setSnackbar({
            isVisible: true,
            title,
            buttonText,
            type,
            duration,
            onButtonClick,
            bottomPosition,
        });
    }, []);

    const hideSnackbar = useCallback(() => {
        setSnackbar((prev) => ({ ...prev, isVisible: false }));
    }, []);

    // 편의 함수들
    const showSuccess = useCallback(
        (title, options = {}) => {
            showSnackbar(title, { ...options, type: "success" });
        },
        [showSnackbar]
    );

    const showError = useCallback(
        (title, options = {}) => {
            showSnackbar(title, { ...options, type: "error" });
        },
        [showSnackbar]
    );

    const showWarning = useCallback(
        (title, options = {}) => {
            showSnackbar(title, { ...options, type: "warning" });
        },
        [showSnackbar]
    );

    const showInfo = useCallback(
        (title, options = {}) => {
            showSnackbar(title, { ...options, type: "info" });
        },
        [showSnackbar]
    );

    const handleClose = useCallback(() => {
        setSnackbar((prev) => ({ ...prev, isVisible: false }));
    }, []);

    const handleButtonClick = useCallback(() => {
        if (snackbar.onButtonClick) {
            snackbar.onButtonClick();
        }
        hideSnackbar();
    }, [snackbar.onButtonClick, hideSnackbar]);

    const value = {
        showSnackbar,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        hideSnackbar,
    };

    return (
        <SnackbarContext.Provider value={value}>
            {children}
            <Snackbar
                isVisible={snackbar.isVisible}
                title={snackbar.title}
                buttonText={snackbar.buttonText}
                type={snackbar.type}
                duration={snackbar.duration}
                bottomPosition={snackbar.bottomPosition}
                onClose={handleClose}
                onButtonClick={handleButtonClick}
            />
        </SnackbarContext.Provider>
    );
};
